import { Box, Drawer, IconButton, Typography } from "@mui/material";
import { motion, AnimatePresence } from "motion/react";
import { X } from "lucide-react";
import { palette } from "../theme";
import { toTimeStr } from "../util/time";
import type { Comment } from "../generated/core-types";

interface UserHistoryDrawerProps {
  open: boolean;
  userName: string;
  comments: Comment[];
  onClose: () => void;
}

export function UserHistoryDrawer({
  open,
  userName,
  comments,
  onClose,
}: UserHistoryDrawerProps): JSX.Element {
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "92vw", sm: 420 },
          maxWidth: 460,
          p: 0,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 3,
          borderBottom: `1px solid ${palette.border}`,
        }}
      >
        <Box>
          <Typography variant="overline" sx={{ color: palette.inkSoft }}>
            Lịch sử
          </Typography>
          <Typography
            sx={{
              fontFamily: '"Fraunces Variable", Georgia, serif',
              fontStyle: "italic",
              fontSize: "1.4rem",
              fontWeight: 500,
              color: palette.ink,
              lineHeight: 1.1,
              mt: "4px",
            }}
          >
            {userName}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: palette.ink,
            border: `1px solid ${palette.border}`,
            borderRadius: "50%",
            width: 36,
            height: 36,
            "&:hover": { borderColor: palette.ink, backgroundColor: palette.surface },
          }}
        >
          <X size={15} strokeWidth={1.8} />
        </IconButton>
      </Box>

      <Box sx={{ p: 3, overflowY: "auto", flex: 1 }}>
        <AnimatePresence initial={false}>
          {comments.length === 0 ? (
            <Typography variant="body2" sx={{ color: palette.inkSoft }}>
              Chưa có bình luận trước đó.
            </Typography>
          ) : (
            comments.map((c, i) => (
              <motion.div
                key={`${c.commentId}_${c.item}_${i}`}
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.04, duration: 0.18 }}
                style={{ marginBottom: 18 }}
              >
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 1,
                    fontFamily: '"JetBrains Mono", monospace',
                    fontSize: "0.72rem",
                    color: palette.inkSoft,
                    letterSpacing: "0.04em",
                  }}
                >
                  {toTimeStr(c.timestamp)} · sản phẩm {String(c.item).padStart(2, "0")}
                </Box>
                <Typography
                  sx={{
                    mt: "6px",
                    fontFamily: '"Geist", system-ui, sans-serif',
                    fontSize: "0.95rem",
                    color: palette.ink,
                    lineHeight: 1.45,
                  }}
                >
                  <Box component="span" sx={{ color: palette.inkSoft, mr: "2px" }}>«</Box>
                  {c.fullComment}
                  <Box component="span" sx={{ color: palette.inkSoft, ml: "2px" }}>»</Box>
                </Typography>
                {i < comments.length - 1 && (
                  <Box
                    sx={{
                      mt: 2.25,
                      width: 36,
                      height: 1,
                      backgroundColor: palette.border,
                    }}
                  />
                )}
              </motion.div>
            ))
          )}
        </AnimatePresence>
      </Box>
    </Drawer>
  );
}
